import helpers from "@/helpers/index.js"
import moment from "moment-timezone"

/**
 * @param {{ value?: [number, number] } | null | undefined} gradeYears
 * @param {(schoolYear: { value?: [number, number] }) => Date} getMinDate
 * @param {(schoolYear: { value?: [number, number] }) => Date} getMaxDate
 * @returns {Array<{ label: string, id: string, range: string | null, startDate?: string, endDate?: string }>}
 */
export function buildBehaviourPointHistoryDateRanges(
  gradeYears,
  getMinDate,
  getMaxDate
) {
  const minDate = getMinDate(gradeYears)
  const selectedSchoolYear = gradeYears?.value

  if (!selectedSchoolYear || !minDate) {
    return [
      {
        label: "Today",
        id: "",
        range: helpers.stringDateRange(moment(), moment())
      }
    ]
  }

  const [startYear, endYear] = selectedSchoolYear
  const today = moment()
  const currentMonth = today.month()
  const minSchoolDate = moment(helpers.minSchoolYearDate())

  const shouldUseStartYear =
    today.isAfter(minSchoolDate) && currentMonth >= 6 && currentMonth <= 11

  const selectedYear = shouldUseStartYear ? startYear : endYear

  const baseDate = moment({
    year: selectedYear,
    month: currentMonth,
    date: today.date()
  })

  const getRange = (days) => {
    const start = baseDate.clone().subtract(days, "days")
    const minSchoolYearDate = moment({
      year: minDate.getFullYear(),
      month: minDate.getMonth(),
      date: minDate.getDate()
    })
    const adjustedStart = start.isBefore(minSchoolYearDate)
      ? minSchoolYearDate
      : start

    return helpers.stringDateRange(adjustedStart, baseDate)
  }

  const maxDate = getMaxDate(gradeYears)

  return [
    {
      label: "Today",
      id: "",
      range: helpers.stringDateRange(baseDate, baseDate)
    },
    {
      label: "Yesterday",
      id: "",
      range: helpers.stringDateRange(
        baseDate.clone().subtract(1, "days"),
        baseDate.clone().subtract(1, "days")
      )
    },
    {
      label: "Last 7 days",
      id: "",
      range: getRange(7)
    },
    {
      label: "Last 14 days",
      id: "",
      range: getRange(14)
    },
    {
      label: "Last 30 days",
      id: "",
      range: getRange(30)
    },
    {
      label: "This month",
      id: "",
      range: helpers.stringDateRange(
        baseDate.clone().startOf("month"),
        baseDate.clone().endOf("month")
      )
    },
    {
      label: "This year",
      id: "",
      range: helpers.currTzDate(minDate) + " - " + helpers.currTzDate(maxDate)
    },
    {
      label: "Custom range",
      range: null,
      startDate: helpers.currTzDate(minDate),
      endDate: helpers.currTzDate(maxDate),
      id: "custom"
    }
  ]
}

/**
 * @param {string | null | undefined} dateRangeValue
 * @returns {{ start: import('moment').Moment | null, end: import('moment').Moment | null }}
 */
export function parseBehaviourPointHistoryDateRange(dateRangeValue) {
  const raw = String(dateRangeValue ?? "").trim()
  if (!raw.includes("-")) {
    return { start: null, end: null }
  }

  const [startRaw, endRaw] = raw.split("-").map((part) => part.trim())
  const start = moment(startRaw, "MM/DD/YYYY", true)
  const end = moment(endRaw, "MM/DD/YYYY", true)

  if (!start.isValid() || !end.isValid()) {
    return { start: null, end: null }
  }

  return {
    start: start.startOf("day"),
    end: end.endOf("day")
  }
}

/**
 * @param {string | null | undefined} occurredAt
 * @param {{ start: import('moment').Moment | null, end: import('moment').Moment | null }} range
 * @returns {boolean}
 */
export function isPointHistoryRowInDateRange(occurredAt, range) {
  if (!range?.start || !range?.end) return true

  const occurred = moment(
    String(occurredAt ?? ""),
    ["MMM D, YYYY h:mm A", "MM/DD/YYYY h:mm A", moment.ISO_8601],
    true
  )
  if (!occurred.isValid()) return true

  return occurred.isBetween(range.start, range.end, undefined, "[]")
}

/**
 * @param {Array<number | string | { value?: number | string }> | null | undefined} selectedGradYears
 * @param {number | string | null | undefined} rowGradYear
 * @returns {boolean}
 */
export function isPointHistoryRowInGradYear(selectedGradYears, rowGradYear) {
  if (!selectedGradYears?.length) return true
  if (rowGradYear == null || rowGradYear === "") return true

  const rowYear = Number(rowGradYear)
  if (Number.isNaN(rowYear)) return true

  return selectedGradYears.some((selected) => {
    const value =
      selected != null && typeof selected === "object"
        ? selected?.value
        : selected
    return Number(value) === rowYear
  })
}

/**
 * @param {string | number | null | undefined} selectedCoreValueId
 * @param {string | number | null | undefined} rowCoreValueId
 * @returns {boolean}
 */
export function isPointHistoryRowInCoreValue(
  selectedCoreValueId,
  rowCoreValueId
) {
  const selected = String(selectedCoreValueId ?? "").trim()
  if (!selected || selected === "all") return true

  return String(rowCoreValueId ?? "") === selected
}

/**
 * @param {{ value?: [number, number] } | null | undefined} gradeYears
 * @param {(schoolYear: { value?: [number, number] }) => Date} getMinDate
 * @param {(schoolYear: { value?: [number, number] }) => Date} getMaxDate
 * @returns {string}
 */
export function getDefaultBehaviourPointHistoryDateRange(
  gradeYears,
  getMinDate,
  getMaxDate
) {
  const minDate = getMinDate(gradeYears)
  const maxDate = getMaxDate(gradeYears)
  if (!minDate || !maxDate) {
    return helpers.stringDateRange(moment(), moment())
  }
  return `${helpers.currTzDate(minDate)} - ${helpers.currTzDate(maxDate)}`
}
